import React, { useState } from "react";
import { Phone, Mail, Globe, MapPin, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import PublicLayout from "../components/PublicLayout";
import { useI18n } from "../i18n/I18nContext";
import { useSite, waLink } from "../context/SiteContext";
import { api, formatApiError } from "../lib/api";

export default function Contact() {
    const { t } = useI18n();
    const { site } = useSite();
    const contact = site?.contact || {};
    const phones = contact.phones || [];
    const emails = contact.emails || [];
    const website = contact.website || "";
    const address = contact.address || "";
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
    const [sending, setSending] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/contact", form);
            toast.success(t("contact.form.success"));
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            toast.error(formatApiError(err) || t("contact.form.error"));
        } finally {
            setSending(false);
        }
    };

    return (
        <PublicLayout>
            <section className="bg-brand-navy text-white" data-testid="contact-hero">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
                    <div className="text-[0.7rem] tracking-[0.3em] uppercase font-semibold text-brand-gold mb-3">
                        {t("nav.contact")}
                    </div>
                    <h1 className="font-display text-5xl sm:text-6xl">{t("contact.title")}</h1>
                    <p className="mt-4 text-white/75 max-w-2xl">{t("contact.subtitle")}</p>
                </div>
            </section>

            <section className="py-20 bg-brand-paper">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
                    {/* Info column */}
                    <div className="lg:col-span-5 space-y-6">
                        <InfoCard icon={Phone} label={t("contact.phones")}>
                            {phones.map((p) => (
                                <a
                                    key={p.waNumber}
                                    href={waLink(p.waNumber)}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`contact-wa-${p.waNumber}`}
                                    className="block hover:text-brand-emerald transition-colors"
                                >
                                    {p.display}
                                </a>
                            ))}
                        </InfoCard>
                        <InfoCard icon={Mail} label={t("contact.email")}>
                            {emails.map((e) => (
                                <a key={e} href={`mailto:${e}`} className="block hover:text-brand-emerald transition-colors">
                                    {e}
                                </a>
                            ))}
                        </InfoCard>
                        <InfoCard icon={Globe} label={t("contact.website")}>
                            <span>{website}</span>
                        </InfoCard>
                        <InfoCard icon={MapPin} label={t("contact.address")}>
                            <span>{address || t("contact.address_value")}</span>
                        </InfoCard>

                        <a
                            href={waLink(site?.socials?.whatsapp_default || phones[0]?.waNumber, "Bonjour GAB'S SERVICES")}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="contact-whatsapp-cta"
                            className="inline-flex items-center justify-center w-full gap-2 px-6 py-4 bg-[#25D366] hover:bg-[#1ebe57] text-white text-[0.72rem] tracking-[0.24em] uppercase font-semibold transition-colors"
                        >
                            <MessageCircle className="w-4 h-4" /> {t("contact.form.whatsapp")}
                        </a>
                    </div>

                    {/* Form */}
                    <form onSubmit={submit} className="lg:col-span-7 bg-white border border-gray-100 p-8 lg:p-10 space-y-5" data-testid="contact-form">
                        <div className="grid sm:grid-cols-2 gap-5">
                            <Field name="name" label={t("contact.form.name")} value={form.name} onChange={onChange} required />
                            <Field name="email" type="email" label={t("contact.form.email")} value={form.email} onChange={onChange} required />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-5">
                            <Field name="phone" label={t("contact.form.phone")} value={form.phone} onChange={onChange} />
                            <Field name="subject" label={t("contact.form.subject")} value={form.subject} onChange={onChange} />
                        </div>
                        <Field name="message" label={t("contact.form.message")} value={form.message} onChange={onChange} required textarea />
                        <button
                            type="submit"
                            disabled={sending}
                            data-testid="contact-submit"
                            className="inline-flex items-center gap-2 px-8 py-4 bg-brand-gold hover:bg-brand-gold-deep disabled:opacity-60 text-white text-[0.72rem] tracking-[0.24em] uppercase font-semibold transition-colors"
                        >
                            <Send className="w-4 h-4" />
                            {sending ? "..." : t("contact.form.send")}
                        </button>
                    </form>
                </div>
            </section>
        </PublicLayout>
    );
}

function Field({ name, label, value, onChange, required, type = "text", textarea }) {
    const cls = "w-full bg-white border border-gray-200 px-4 py-3 text-sm text-brand-navy focus:outline-none focus:border-brand-gold transition-colors";
    return (
        <label className="block">
            <span className="text-[0.65rem] tracking-[0.22em] uppercase font-bold text-brand-navy">
                {label}
                {required ? <span className="text-brand-gold"> *</span> : null}
            </span>
            <div className="mt-1.5">
                {textarea ? (
                    <textarea
                        name={name}
                        value={value}
                        onChange={onChange}
                        required={required}
                        rows={5}
                        data-testid={`field-${name}`}
                        className={cls + " resize-y min-h-[140px]"}
                    />
                ) : (
                    <input
                        type={type}
                        name={name}
                        value={value}
                        onChange={onChange}
                        required={required}
                        data-testid={`field-${name}`}
                        className={cls}
                    />
                )}
            </div>
        </label>
    );
}

function InfoCard({ icon: Icon, label, children }) {
    return (
        <div className="bg-white border border-gray-100 p-5 flex gap-4 items-start">
            <div className="w-11 h-11 bg-brand-emerald/10 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-brand-emerald" strokeWidth={1.5} />
            </div>
            <div>
                <div className="text-[0.65rem] tracking-[0.22em] uppercase font-bold text-brand-gold mb-1">{label}</div>
                <div className="text-brand-navy text-sm leading-relaxed">{children}</div>
            </div>
        </div>
    );
}
